'use strict';

const {
	AddToCartOutcomeKind,
	addProductToCart,
	classifyAddToCartResult,
	emptyCart,
} = require('public/js/components/cart-store');
const { clearSelectedVehicle, handleGarageData, setSelectedVehicle } = require('public/js/components/garage-store');
const inputSuggestions = require('public/js/components/input-suggestions');
const utils = require('public/js/helpers/utils');
const {
	CSRF_FAILURE_ERROR_CODE,
	CSRF_FAILURE_ERROR_HEADER,
} = require('public/js/lib/csrf/ajax-hooks');
const csrf = require('public/js/lib/csrf/utils');
const { loadingIntent } = require('public/js/lib/intents');

// `cart_source` for analytics attribution. Stamped here (not in
// cart-store) so the store stays caller-agnostic.
const CART_SOURCE = 'shopping-agent';
const INTENT_AGENT_START = 'agent.start';
const INTENT_TRIGGER_SELECTOR = '[data-rp-intent-trigger="' + INTENT_AGENT_START + '"]';
const SDK_MANIFEST_URL = 'https://cdn-resources.revolutionparts.io/shopping-agent-ui/latest.json';
const SDK_SCRIPT_SRC = 'https://cdn-resources.revolutionparts.io/shopping-agent-ui/scout.js';
const SESSION_STORAGE_KEY = 'rp_agent_active';
const TOKEN_ENDPOINT = '/ajax/agent/token';
const SEARCH_INPUT_SELECTOR = '.search-bar-component .main-search-wrapper input[name="search_str"]';

const _state = {
	token: null,
	sessionId: null,
	// Blocks repeat starts after a successful boot; concurrent-start
	// coalescing happens on `_inflightStart` (below).
	active: false,
	// Set on fresh `startSession()` from the CTA's `data-rp-intent-type`;
	// surfaced via `getSession()` for the SDK handshake. Null on restore.
	intent: null,
	// Hydrated lazily on first `getSession()`; `vehicleLoaded`
	// short-circuits subsequent reads.
	vehicle: null,
	vehicleLoaded: false,
};

/** @type {Promise<void>|null} */
let _inflightStart = null;
/** @type {Promise<object|null>|null} */
let _inflightVehicle = null;

/**
 * Decodes the payload of a JWT without verifying the signature.
 * @param {string} jwt
 * @returns {{ exp: number, iat: number, store_hash: string, visitor_id: string, iss: string, aud: string }}
 */
function decodeTokenPayload(jwt) {
	const parts = jwt.split('.');
	if (parts.length !== 3) {
		throw new Error('Invalid JWT format');
	}

	const base64 = parts[1].replaceAll('-', '+').replaceAll('_', '/');
	const jsonStr = atob(base64);

	return JSON.parse(jsonStr);
}

/**
 * Reads the CSRF token from the meta tag. Thin wrapper around
 * `csrf.getCsrfTokenFromMeta` kept on the facade's exported surface
 * for back-compat with existing test fixtures.
 *
 * @returns {string}
 */
function getCsrfToken() {
	return csrf.getCsrfTokenFromMeta();
}

/**
 * Waits for the CSRF bootstrap to finish populating the CSRF meta tag.
 * Re-exported from `csrf.waitForCsrf` for back-compat.
 *
 * @returns {Promise<void>}
 */
function waitForCsrf() {
	return csrf.waitForCsrf();
}

/**
 * Fetches a JWT from the token endpoint.
 * @returns {Promise<string>}
 */
function fetchToken() {
	if (typeof fetch !== 'function') {
		return Promise.reject(new Error('fetch is not available'));
	}

	if (utils.isBot()) {
		return Promise.reject(new Error('token fetch skipped for bot user agent'));
	}

	return csrf.fetchWithCsrf(TOKEN_ENDPOINT, {
		method: 'POST',
		credentials: 'same-origin',
		headers: { 'Accept': 'application/json' },
	})
		.then(function (response) {
			if (!response.ok) {
				const err = new Error('Token request failed: ' + response.status);
				err.status = response.status;
				err.isCsrfFailure = response.status === 403
					&& response.headers.get(CSRF_FAILURE_ERROR_HEADER)?.toLowerCase() === CSRF_FAILURE_ERROR_CODE;
				throw err;
			}
			return response.json();
		})
		.then(function (data) {
			if (!data.token || typeof data.token !== 'string') {
				throw new Error('Invalid token response');
			}
			return data.token;
		});
}

/**
 * Computes the opaque session ID from JWT claims.
 * @param {{ store_hash: string, visitor_id: string }} payload
 * @returns {string}
 */
function computeSessionId(payload) {
	return payload.store_hash + ':' + payload.visitor_id;
}

/**
 * Returns the current token, fetching a new one if expired or missing.
 * @returns {Promise<string>}
 */
function getToken() {
	if (_state.token) {
		try {
			const payload = decodeTokenPayload(_state.token);
			const now = Math.floor(Date.now() / 1000);
			if (payload.exp > now) {
				return Promise.resolve(_state.token);
			}
		} catch (e) {
			console.warn('[RPShoppingAgent] Cached token failed to decode, refreshing:', e);
		}
	}
	return fetchToken().then(function (token) {
		_state.token = token;
		return token;
	});
}

/**
 * Returns the session ID, or null if no token has been fetched yet.
 * @returns {string|null}
 */
function getSessionId() {
	return _state.sessionId;
}

/**
 * Picks the car flagged `selected: true` out of the garage payload.
 * @param {unknown} garage
 * @returns {object|null}
 */
function findSelectedCar(garage) {
	if (!Array.isArray(garage)) {
		return null;
	}
	for (const car of garage) {
		if (car?.selected === true) {
			return car;
		}
	}
	return null;
}

/**
 * Returns the currently-selected vehicle (or null). Piggybacks on the
 * vehicle picker's existing garage loader so we don't duplicate the
 * AJAX call. `setVehicle` / `resetVehicle` write through the cache.
 * Concurrent callers share the same in-flight promise. Never rejects.
 *
 * @returns {Promise<object|null>}
 */
function fetchSelectedVehicle() {
	if (_state.vehicleLoaded) {
		return Promise.resolve(_state.vehicle);
	}
	if (_inflightVehicle !== null) {
		return _inflightVehicle;
	}

	// Capture `pending` so resolvers can detect a torn-down or replaced
	// session (stopSession / a fresh getSession reassigns _inflightVehicle)
	// and bail rather than write through to a dead session.
	const pending = handleGarageData.whenGarageReady().then(function (garage) {
		if (_inflightVehicle !== pending) {
			return null;
		}
		_inflightVehicle = null;
		// `whenGarageReady` returns `null` for both stable (contextual
		// loading disabled) and transient (`rp$` not yet loaded) cases.
		// Don't cache the null answer — leave `vehicleLoaded` false so
		// the next call retries. Cheap when stable, correct when transient.
		if (!Array.isArray(garage)) {
			return null;
		}
		const vehicle = findSelectedCar(garage);
		_state.vehicle = vehicle;
		_state.vehicleLoaded = true;
		return vehicle;
	}).catch(function () {
		// Don't cache null — same rationale as the null-array path.
		if (_inflightVehicle === pending) {
			_inflightVehicle = null;
		}
		return null;
	});
	_inflightVehicle = pending;

	return _inflightVehicle;
}

/**
 * Returns the first non-empty main search-bar query (live DOM read).
 * Pages may render multiple `.search-bar-component` instances (e.g. header
 * and mobile); when more than one has text they carry the same value.
 *
 * @returns {string}
 */
function readSearchQuery() {
	const inputs = document.querySelectorAll(SEARCH_INPUT_SELECTOR);
	for (const input of inputs) {
		if (input instanceof HTMLInputElement) {
			const trimmed = input.value.trim();
			if (trimmed !== '') {
				return trimmed;
			}
		}
	}
	return '';
}

/**
 * Returns the current session credentials, fetching a new token if the
 * cached one is expired or absent. The handshake call AllCloud's SDK
 * makes to obtain `{ token, sessionId, vehicle, intent, search }`. Works
 * whether or not `startSession()` has run — derives `sessionId` from the
 * token's claims as a fallback.
 *
 * @returns {Promise<{ token: string, sessionId: string, vehicle: object|null, intent: string|null, search: string }>}
 */
function getSession() {
	return Promise.all([getToken(), fetchSelectedVehicle()]).then(function (results) {
		const token = results[0];
		const vehicle = results[1];
		if (!_state.sessionId) {
			_state.sessionId = computeSessionId(decodeTokenPayload(token));
		}
		return {
			token: token,
			sessionId: _state.sessionId,
			vehicle: vehicle,
			intent: _state.intent,
			search: readSearchQuery(),
		};
	});
}

/**
 * Sets the session-active flag in sessionStorage.
 */
function markSessionActive() {
	try {
		sessionStorage.setItem(SESSION_STORAGE_KEY, '1');
	} catch (e) {
		console.warn('[RPShoppingAgent] sessionStorage unavailable, session will not persist:', e);
	}
}

/**
 * Ends the current shopping-agent session: clears the persistence flag AND
 * resets in-memory state so a subsequent `startSession()` / `requestStart()`
 * is a fresh start rather than a no-op.
 */
function stopSession() {
	_state.token = null;
	_state.sessionId = null;
	_state.active = false;
	_state.intent = null;
	_state.vehicle = null;
	_state.vehicleLoaded = false;
	_inflightVehicle = null;
	try {
		sessionStorage.removeItem(SESSION_STORAGE_KEY);
	} catch (e) {
		console.warn('[RPShoppingAgent] sessionStorage unavailable:', e);
	}
}

/**
 * Maps a write-envelope failure to a human-readable error string.
 * Prefers `data.message` (server-side), falls back to `error`
 * (client/network), then to a status-code string.
 *
 * @param {{ ok: boolean, status: number, data: object|null, error: string|null }} result
 * @returns {string}
 */
function errorMessageFromWriteResult(result) {
	if (typeof result.data?.message === 'string') {
		return result.data.message;
	}
	if (typeof result.error === 'string' && result.error !== '') {
		return result.error;
	}
	return 'request failed: ' + result.status;
}

/**
 * Sets the selected vehicle. Thin adapter over `garage-store.setSelectedVehicle`,
 * which handles the network call, cache invalidation, and refetch. Never
 * reloads the page — that's the UX point of the facade. Stores without
 * `hasOnlyYmmPartSources` reject incomplete picks server-side.
 *
 * @param {{ year: string|number, make: string, model: string, trim?: string, engine?: string }} vehicle
 * @returns {Promise<{ success: boolean, carId?: string, error?: string }>}
 */
function setVehicle(vehicle) {
	return setSelectedVehicle(vehicle).then(function (result) {
		if (!result.ok) {
			return { success: false, error: errorMessageFromWriteResult(result) };
		}

		// `GarageController::select` returns 200 with `vehicle: null` when
		// `GarageALI::setSelectedCar` hits its recently-removed-car guard.
		// Treat as failure so the SDK doesn't see "success but no carId".
		if (!result.data?.vehicle || typeof result.data.vehicle !== 'object') {
			return { success: false, error: 'vehicle was not selected' };
		}

		const selectedVehicle = findSelectedCar(result.garage);

		if (selectedVehicle) {
			_state.vehicle = selectedVehicle;
			_state.vehicleLoaded = true;
		} else {
			// POST succeeded but the refetch didn't confirm — leave the
			// cache unloaded so the next getSession() retries hydration.
			_state.vehicleLoaded = false;
		}
		// Invalidates any in-flight fetchSelectedVehicle so its resolver
		// bails rather than clobbering the just-written cache.
		_inflightVehicle = null;

		const payload = { success: true };
		// Prefer the shape-canonical hash from the refetch; fall back to
		// the controller's hash when the read-back didn't return a car.
		let hash = null;
		if (typeof selectedVehicle?.hash === 'string') {
			hash = selectedVehicle.hash;
		} else if (typeof result.data.vehicle.hash === 'string') {
			hash = result.data.vehicle.hash;
		}
		if (hash !== null) {
			payload.carId = hash;
		}
		return payload;
	}).catch(function (err) {
		console.warn('[RPShoppingAgent] setVehicle failed unexpectedly:', err);
		return { success: false, error: err?.message || 'unexpected failure' };
	});
}

/**
 * Clears the selected vehicle. Thin adapter over
 * `garage-store.clearSelectedVehicle` (which preserves garage entries —
 * important for DMS-mirrored garages like Stellantis/AutoNation).
 * Translates the envelope to the agent SDK's `{ success, error? }` shape
 * and clears the in-facade vehicle cache.
 *
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
function resetVehicle() {
	return clearSelectedVehicle().then(function (result) {
		if (!result.ok) {
			return { success: false, error: errorMessageFromWriteResult(result) };
		}

		_state.vehicle = null;
		_state.vehicleLoaded = true;
		// Mirrors `setVehicle`'s _inflightVehicle invalidation.
		_inflightVehicle = null;

		return { success: true };
	}).catch(function (err) {
		console.warn('[RPShoppingAgent] resetVehicle failed unexpectedly:', err);
		return { success: false, error: err?.message || 'unexpected failure' };
	});
}

/**
 * Builds the SDK's `cartSummary` envelope from a cart body. Non-cart
 * 2xx responses resolve as `success: true` with no summary.
 *
 * `subtotalAmount` is `Cart::getSubtotalAmount()` — a decimal scalar.
 * Stringified defensively for the SDK's `string` contract; `?? ''`
 * prevents null/undefined becoming literal `'null'` / `'undefined'`.
 *
 * @param {object|null} data
 * @returns {{ success: true, cartSummary?: { itemCount: number, subtotal: string } }}
 */
function buildCartSuccess(data) {
	if (data?.id === undefined || !Array.isArray(data?.items)) {
		return { success: true };
	}
	const itemCount = typeof data.itemsCount === 'number' ? data.itemsCount : 0;
	const subtotal = String(data.subtotalAmount ?? '');
	return {
		success: true,
		cartSummary: {
			itemCount: itemCount,
			subtotal: subtotal
		}
	};
}

/**
 * Maps a cart-domain `AddToCartOutcome` to the SDK's `addToCart` shape.
 * The outcome `kind` strings align with the SDK's `errorCode` values by
 * design; remap here if the vocabularies ever diverge.
 *
 * @param {{ ok: boolean, status: number, data: object|null, error: string|null }} result
 * @returns {{ success: boolean, cartSummary?: { itemCount: number, subtotal: string }, error?: string, errorCode?: string }}
 */
function mapAddToCartResult(result) {
	const outcome = classifyAddToCartResult(result);
	if (outcome.kind === AddToCartOutcomeKind.SUCCESS) {
		return buildCartSuccess(outcome.cart);
	}
	return outcome.kind === AddToCartOutcomeKind.FAILURE
		? { success: false, error: outcome.message }
		: { success: false, error: outcome.message, errorCode: outcome.kind };
}

/**
 * Adds a product to the cart. Thin adapter over `cart-store.addProductToCart`,
 * which handles the network call and header-cart refresh. Maps the four
 * HLD failure modes onto `errorCode`.
 *
 * V1 does not support variant/option selection — the agent's tool layer
 * is responsible for filtering products with non-empty `variants` before
 * calling this method.
 *
 * @param {{ sku: string, catalog: string, quantity?: number }} product
 * @returns {Promise<{ success: boolean, cartSummary?: { itemCount: number, subtotal: string }, error?: string, errorCode?: string }>}
 */
function addToCart(product) {
	return addProductToCart({ ...product, cartSource: CART_SOURCE })
		.then(mapAddToCartResult)
		.catch(function (err) {
			console.warn('[RPShoppingAgent] addToCart failed unexpectedly:', err);
			return { success: false, error: err?.message || 'unexpected failure' };
		});
}

/**
 * Empties the cart. Thin adapter over `cart-store.emptyCart`, which
 * handles the network call and header-cart refresh. Treats the
 * server's `{ status: 'fail' }` (no cart to clear) as **success** —
 * `resetCart` is idempotent by intent.
 *
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
function resetCart() {
	return emptyCart().then(function (result) {
		if (!result.ok) {
			return { success: false, error: errorMessageFromWriteResult(result) };
		}
		return { success: true };
	}).catch(function (err) {
		console.warn('[RPShoppingAgent] resetCart failed unexpectedly:', err);
		return { success: false, error: err?.message || 'unexpected failure' };
	});
}

/**
 * Checks whether the agent chat was open before this page load.
 * @returns {boolean}
 */
function wasSessionActive() {
	try {
		return sessionStorage.getItem(SESSION_STORAGE_KEY) === '1';
	} catch (e) {
		console.warn('[RPShoppingAgent] sessionStorage unavailable:', e);
		return false;
	}
}

/**
 * Injects the AllCloud SDK loader script. The SDK calls back into
 * `RPShoppingAgent.getSession()` for credentials (including intent),
 * so token/sessionId aren't passed as data attributes. `restore` is
 * needed pre-handshake. Idempotent.
 *
 * @param {{ restore: boolean }} config
 */
function initAllCloudSdk(config) {
	if (document.querySelector('script[src="' + SDK_SCRIPT_SRC + '"]')) {
		return;
	}

	const script = document.createElement('script');
	script.src = SDK_SCRIPT_SRC;
	script.async = true;
	script.dataset.widget = '1';
	script.dataset.manifestUrl = SDK_MANIFEST_URL;
	script.dataset.restore = config.restore ? '1' : '0';
	document.head.appendChild(script);
}

/**
 * Starts (or restores) the shopping agent session.
 * Fetches a token, computes session ID, and boots the SDK.
 *
 * Idempotent in two senses:
 *   - If a previous start has already succeeded (`_state.active`), returns a
 *     resolved promise immediately (no token re-fetch or SDK re-boot). A
 *     non-restore call still updates `_state.intent` so chip clicks can
 *     switch flows on an open session.
 *   - If a start is currently in-flight, returns the SAME promise so all
 *     callers resolve together when the underlying work completes.
 *
 * @param {{ restore?: boolean, intent?: string|null }} [options]
 * @returns {Promise<void>}
 */
function startSession(options) {
	const restore = Boolean(options?.restore);
	const intent = options?.intent ?? null;

	if (!restore) {
		_state.intent = intent;
	}

	if (_state.active) {
		return Promise.resolve();
	}
	if (_inflightStart) {
		return _inflightStart;
	}

	_inflightStart = fetchToken()
		.then(function (token) {
			_state.token = token;
			const payload = decodeTokenPayload(token);

			_state.sessionId = computeSessionId(payload);

			markSessionActive();

			initAllCloudSdk({ restore: restore });
			_state.active = true;
		})
		.catch(function (err) {
			if (!restore && err.isCsrfFailure) {
				markSessionActive();
				globalThis.location.reload();
				return;
			}
			stopSession();
			console.error('[RPShoppingAgent] Session start failed:', err);
		})
		.finally(function () {
			_inflightStart = null;
		});

	return _inflightStart;
}

/**
 * Public wrapper for entry-point CTAs. Closes any open auto-suggest
 * popup, then starts a fresh session (never a restore). Forwards the
 * CTA's `data-rp-intent-type` (surfaced via `getSession().intent`).
 *
 * @param {{ intent?: string|null }} [options]
 * @returns {Promise<void>}
 */
function requestStart(options) {
	inputSuggestions.closeAll();
	return startSession({ restore: false, intent: options?.intent ?? null });
}

/**
 * Binds a single delegated click listener for elements marked with
 * `data-rp-intent-trigger="agent.start"`. Reads `data-rp-intent-type` off
 * the trigger so chip-style CTAs carry their variant through. Uses capture
 * phase so `_state.intent` is updated before the AllCloud SDK's bubble-
 * phase handler calls `getSession()`. Idempotent across page lifetime.
 */
function bindAgentTriggers() {
	if (globalThis.__rp_agent_triggers_bound) {
		return;
	}
	globalThis.__rp_agent_triggers_bound = true;

	document.addEventListener('click', (event) => {
		const triggerEl = event.target.closest(INTENT_TRIGGER_SELECTOR);
		if (triggerEl) {
			requestStart({ intent: triggerEl.dataset.rpIntentType || null });
		}
	}, true);
}

/**
 * Runs on module load.
 *
 * Resolution order:
 *   1. Pre-load CTA click recorded by `lib/intents` → fresh start.
 *   2. Otherwise, if a session was active before this navigation → restore.
 *
 * Always binds a post-load click listener so subsequent CTA clicks during
 * this page lifetime call `requestStart()`.
 */
function init() {
	bindAgentTriggers();

	const pendingIntent = loadingIntent[INTENT_AGENT_START];
	if (pendingIntent) {
		delete loadingIntent[INTENT_AGENT_START];
		requestStart({ intent: pendingIntent.type });
	} else if (wasSessionActive()) {
		startSession({ restore: true });
	}
}

module.exports = {
	addToCart: addToCart,
	computeSessionId: computeSessionId,
	decodeTokenPayload: decodeTokenPayload,
	getCsrfToken: getCsrfToken,
	getSession: getSession,
	getSessionId: getSessionId,
	getToken: getToken,
	markSessionActive: markSessionActive,
	requestStart: requestStart,
	resetCart: resetCart,
	resetVehicle: resetVehicle,
	setVehicle: setVehicle,
	startSession: startSession,
	stopSession: stopSession,
	waitForCsrf: waitForCsrf,
	wasSessionActive: wasSessionActive,
};

// Public runtime API — narrower than module.exports. Implementation
// details like `markSessionActive` / `getToken` stay off this surface;
// callers go through `getSession` / `stopSession` instead.
globalThis.RPShoppingAgent = {
	addToCart: module.exports.addToCart,
	getSession: module.exports.getSession,
	getSessionId: module.exports.getSessionId,
	requestStart: module.exports.requestStart,
	resetCart: module.exports.resetCart,
	resetVehicle: module.exports.resetVehicle,
	setVehicle: module.exports.setVehicle,
	startSession: module.exports.startSession,
	stopSession: module.exports.stopSession,
};

init();
